<template>
  <div class="flex-div justify-center mt-24 md:mt-48 my-10">
    <h2 class="heading text-xl md:text-4xl">{{ $t("features.title") }}</h2>
  </div>
  <div class="flex justify-center gap-2">
    <button
      :class="[
        'w-20',
        'h-9',
        'rounded-md',
        'tab-btn',
        { 'active-btn': isActive === 'Mobile' },
      ]"
      @click="onMobileClick()"
    >
      {{ $t("features.tab.mobile") }}
    </button>
    <button
      :class="[
        'w-20',
        'h-9',
        'rounded-md',
        'tab-btn',
        { 'active-btn': isActive === 'Web' },
      ]"
      @click="onWebClick()"
    >
      {{ $t("features.tab.web") }}
    </button>
  </div>

  <div class="hidden md:flex justify-center">
    <div class="features-grid flex flex-col">
        <FeatureCard
          title="features.realtimeTracking"
          :icon="'ri-truck-line'"
          :right="true"
          desc="features.realtimeTracking.desc"
        />
        <FeatureCard
          title="features.proposalBuilder"
          :icon="`ri-file-text-line`"
          :right="true"
          desc="features.proposalBuilder.desc"
        />
        <FeatureCard
          title="features.stormMaps"
          :icon="`ri-road-map-line`"
          :right="true"
          desc="features.stormMaps.desc"
        />
      </div>

    <!-- Mobile Image (Center) -->
    <div class="max-w-2xl">
      <img
        v-if="isActive === 'Mobile'"
        :src="imageSrc('Mobile')"
        alt="Mobile View"
        class="max-h-[48rem]"
      />
      <img v-else :src="imageSrc('Web')" alt="Web View" />
    </div>

    <!-- Right Features -->
    <div class="features-grid flex flex-col">
      <FeatureCard
        title="features.livePricing"
        :icon="`ri-store-3-line`"
        :right="false"
        desc="features.livePricing.desc"
      />
      <FeatureCard
        title="features.easyOrdering"
        :icon="`ri-box-3-line`"
        :right="false"
        desc="features.easyOrdering.desc"
      />
      <FeatureCard
        title="features.invoiceHistory"
        :icon="`ri-time-line`"
        :right="false"
        desc="features.invoiceHistory.desc"
      />
    </div>
  </div>

  <!-- Feature card display for mobile -->
  <div class="md:hidden">
    <div class="mt-8">
      <img
        v-if="isActive === 'Mobile'"
        :src="imageSrc('Mobile')"
        alt="Mobile View"
      />
      <img v-else :src="imageSrc('Web')" alt="Web View" />
    </div>
    <div class="-mx-5 relative flex features-grid gap-3 overflow-x-auto no-scrollbar scroll-smooth">
      <div
        v-for="(feature, index) in featuresData"
        :key="index"
        class="feature-card"
        @click="handleMouseClick(index)">
        <FeatureCard
          :title="feature.title"
          :icon="feature.icon"
          :right="false"
          :desc="feature.desc"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
    import { ref, computed } from "vue";
    import FeatureCard from "./FeatureCard.vue"
    import { MEDIA_ASSET_BASE_URL, MEDIA_ASSET_LANDING_PATH } from "./../../../config/constants.js";
    import { useI18n } from 'vue-i18n';

    const { locale } = useI18n();

    const isActive = ref("Mobile");

    const imageSrc = computed(() => {
        const suffix = locale.value === 'es' ? 'ES' : '';
        return (isActive) =>
            `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_LANDING_PATH}AppFeatures${isActive}${suffix}.png`;
    });

    const onMobileClick = () => {
        isActive.value = "Mobile";
    };

    const onWebClick = () => {
        isActive.value = "Web";
    };

    const handleMouseClick = (index) => {
        const tabs = document.querySelectorAll(".feature-card");
        tabs.forEach((tab, i) => {
            if (i === index) {
                tab.classList.add("active");
            } else {
                tab.classList.remove("active");
            }
        });
    }

    const featuresData = [
        {
            title: "features.realtimeTracking",
            icon: "ri-truck-line",
            right: true,
            desc: "features.realtimeTracking.desc"
        },
        {
            title: "features.proposalBuilder",
            icon: "ri-file-text-line",
            right: true,
            desc: "features.proposalBuilder.desc"
        },
        {
            title: "features.stormMaps",
            icon: "ri-road-map-line",
            right: true,
            desc: "features.stormMaps.desc"
        },
        {
            title: "features.livePricing",
            icon: "ri-store-3-line",
            right: false,
            desc: "features.livePricing.desc"
        },
        {
            title: "features.easyOrdering",
            icon: "ri-box-3-line",
            right: false,
            desc: "features.easyOrdering.desc"
        },
        {
            title: "features.invoiceHistory",
            icon: "ri-time-line",
            right: false,
            desc: "features.invoiceHistory.desc"
        },
    ];


</script>

<style scoped>
.flex-div {
  width: 100%;
  display: flex !important;
  align-items: center;
}

.tab-btn {
  border: 2px solid #ccc;
  background-color: white;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.tab-btn:hover {
  border-color: #B43235;
  color: #B43235;
}

.active-btn {
  border-color: #B43235 !important;
  color: #B43235 !important;
  border: 2px solid #B43235;
  font-weight: bold;
  background-color: white;
}

.features-grid {
  color: #171717;
}

.heading {
  font-weight: 500;
  text-align: center;
  color: #2e2e2e;
  line-height: 1.3;
  padding-bottom: 10px;
  line-height: 1.3;
}
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}

.e-card {
  border-radius: 0 0 10px 10px;
}
.active .e-card:hover,
.active .e-card:active {
  outline: none;
  border-radius: 0 0 10px 10px;
  border:solid #762123;
  border-width: 3px 0 0 0;
}
</style>
